'use client';
import { useEffect, useRef } from 'react';
import { useAnnouncements, useMarkAnnouncementsAsViewed, useMyAnnouncements } from '@/hooks';
import { useAuth } from '@/providers';
import { useViewStore } from '@/stores';
import AnnouncementsModal from './announcements-modal';

export default function AnnouncementsModalManager() {
  const { isLoggedIn } = useAuth();
  const {
    showAnnouncementsModal,
    announcementModalType,
    toggleAnnouncementsModal,
    hasShownAnnouncements,
    setHasShownAnnouncements,
  } = useViewStore();

  // Only load user announcements if we need to check for auto-showing or modal is open
  const shouldLoadMyAnnouncements = !hasShownAnnouncements || showAnnouncementsModal;
  const { data: myAnnouncements = [] } = useMyAnnouncements(shouldLoadMyAnnouncements);

  // Only load all announcements when modal is open and showing all announcements
  const shouldLoadAllAnnouncements = showAnnouncementsModal && announcementModalType === 'all';
  const { data: allAnnouncements = [] } = useAnnouncements(shouldLoadAllAnnouncements);

  const { mutate: markAsViewed, isPending: markingAsViewed } = useMarkAnnouncementsAsViewed();

  // Use appropriate announcements based on modal type
  const announcements = announcementModalType === 'all' ? allAnnouncements : myAnnouncements;

  // Track if user manually dismissed the modal to prevent auto-reopening
  const manuallyDismissedRef = useRef(false);

  // Auto-show announcements modal only once per page load/session
  useEffect(() => {
    if (
      isLoggedIn &&
      myAnnouncements.length > 0 &&
      !showAnnouncementsModal &&
      !manuallyDismissedRef.current &&
      !hasShownAnnouncements
    ) {
      toggleAnnouncementsModal('user');
      setHasShownAnnouncements(true);
    }
  }, [
    isLoggedIn,
    myAnnouncements.length,
    showAnnouncementsModal,
    toggleAnnouncementsModal,
    hasShownAnnouncements,
    setHasShownAnnouncements,
  ]);

  // Auto-close announcements modal when user announcements become empty (after marking as viewed)
  useEffect(() => {
    if (
      showAnnouncementsModal &&
      announcementModalType === 'user' &&
      myAnnouncements.length === 0
    ) {
      toggleAnnouncementsModal();
      manuallyDismissedRef.current = false; // Reset for future announcements
    }
  }, [
    showAnnouncementsModal,
    announcementModalType,
    myAnnouncements.length,
    toggleAnnouncementsModal,
  ]);

  // Reset manual dismissal flag when new user announcements arrive
  useEffect(() => {
    if (myAnnouncements.length > 0) {
      manuallyDismissedRef.current = false;
    }
  }, [myAnnouncements.length]);

  const handleCloseAnnouncementsModal = () => {
    manuallyDismissedRef.current = true;
    toggleAnnouncementsModal();
  };

  return (
    <AnnouncementsModal
      open={showAnnouncementsModal}
      onClose={handleCloseAnnouncementsModal}
      announcements={announcements}
      onMarkAsViewed={announcementModalType === 'user' ? () => markAsViewed() : undefined}
      isLoading={markingAsViewed}
    />
  );
}
